import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import './Dashboard.css'; // ✅ import your CSS styles
import ReactModal from 'react-modal';

const DISPLAY_EDGE = 1000;   // carousel / grid rendering
const PREVIEW_EDGE = 2400;   // click-to-zoom full view

// Safari cannot encode WebP from a canvas, and toBlob is spec'd to silently
// return a PNG when it doesn't support the requested type. A PNG at these
// dimensions would be bigger than the JPEG we're replacing, so probe the
// encoder once and fall back to JPEG rather than trusting the request.
type Encoder = { mime: string; ext: string };

const ENCODER_PREFERENCE: Encoder[] = [
  { mime: 'image/webp', ext: 'webp' },
  { mime: 'image/jpeg', ext: 'jpg' },
];

let cachedEncoder: Encoder | null = null;

async function pickEncoder(): Promise<Encoder> {
  if (cachedEncoder) return cachedEncoder;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  for (const encoder of ENCODER_PREFERENCE) {
    const probe = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, encoder.mime, 0.8)
    );
    if (probe?.type === encoder.mime) {
      cachedEncoder = encoder;
      return encoder;
    }
  }
  // JPEG is universally supported; treat it as the floor.
  cachedEncoder = ENCODER_PREFERENCE[ENCODER_PREFERENCE.length - 1];
  return cachedEncoder;
}

async function resizeImage(
  file: File,
  maxEdge: number,
  quality: number
): Promise<{ blob: Blob; ext: string; mime: string }> {
  const encoder = await pickEncoder();
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      encoder.mime,
      quality
    )
  );
  // Trust what came back, not what we asked for.
  return { blob, ext: encoder.ext, mime: blob.type || encoder.mime };
}

export default function Dashboard() {
  const [user, setUser] = useState<import('@supabase/supabase-js').User | null>(null);
  const [cars, setCars] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
  Inspection: false,
  Fixing: false,
  'Ready For Sale': false,
});
  const [selectedCar, setSelectedCar] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [carImages, setCarImages] = useState<any[]>([]);
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);


  const fetchCarsAndImages = async () => {
    const { data, error } = await supabase
      .from('cars')
      .select('*')
      .order('dateBought', { ascending: false });

    if (error) {
      console.error('Error loading cars:', error.message);
    } else {
      setCars(data || []);
    }

  };

  useEffect(() => {
    const init = async () => {
      const { data: userData } = await supabase.auth.getUser();
      setUser(userData?.user ?? null);
      if (userData?.user) {
        await fetchCarsAndImages();
      }
      setLoading(false);
    };
    init();
  }, []);

  const totalInvested = cars.reduce((sum, car) => {
    if (car.state === 'Sold') return sum;
    const price = parseFloat(car.purchasePrice);
    return sum + (isNaN(price) ? 0 : price);
  }, 0);

  const inspectionValue = cars.reduce((sum, car) => {
  const price = parseFloat(car.purchasePrice);
  return car.state === 'Inspection' && !isNaN(price) ? sum + price : sum;
  }, 0);

  const fixingValue = cars.reduce((sum, car) => {
  const price = parseFloat(car.purchasePrice);
  return car.state === 'Fixing' && !isNaN(price) ? sum + price : sum;
  }, 0);

  const rfsValue = cars.reduce((sum, car) => {
  const price = parseFloat(car.purchasePrice);
  return car.state === 'Ready For Sale' && !isNaN(price) ? sum + price : sum;
  }, 0);

  const totalCarsOwned = cars.filter(car => car.state !== 'Sold').length;
  const inspectionCount = cars.filter(car => car.state === 'Inspection').length;
  const fixingCount = cars.filter(car => car.state === 'Fixing').length;
  const rfsCount = cars.filter(car => car.state === 'Ready For Sale').length;

  const activeStates = Object.entries(filters)
  .filter(([_, isActive]) => isActive)
  .map(([state]) => state);

  const filteredCars = cars.filter((car) => {
  const query = searchQuery.toLowerCase();
  const matchesSearch =
    car.make?.toLowerCase().includes(query) ||
    car.model?.toLowerCase().includes(query) ||
    car.vin?.toLowerCase().includes(query) ||
    car.stock?.toString().toLowerCase().includes(query) ||
    car.year?.toString().includes(query);

  const matchesState =
    (activeStates.length === 0 && car.state !== 'Sold') ||
    activeStates.includes(car.state);

  return matchesSearch && matchesState;
  });

  if (!user) return <div className="dashboard-container"><p>Please log in.</p></div>;
  if (loading) return <div className="dashboard-container"><p>Loading...</p></div>;

  return (
    <>
      <div className="dashboard-container">

      <div className="dashboard-title-row">
        <h1 className="dashboard-title">BBA Dashboard</h1>
        <button
          className="add-car-button"
          onClick={() => {
            const emptyCar = {
              vin: '',
              state: 'Inspection',
              year: '',
              make: '',
              model: '',
              dateBought: '',
              dateSold: '',
              purchasePrice: '',
              salePrice: '',
              acquiredFrom: '',
              notes: '',
              onLot: false,
              onWeb: false,
              onCL: false,
            };
            setEditForm(emptyCar);
            setSelectedCar({ isNew: true }); // mark as new
            setCarImages([]);
            setPendingImageFiles([]);
            setImagesToDelete([]);
          }}
        >
          + Add Car
        </button>
      </div>

      <h2>Summary</h2>
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-value">${totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="summary-label">Total Invested in Cars</div>
        </div>
        <div className="summary-card">
          <div className="summary-value-inspection">{totalCarsOwned}</div>
          <div className="summary-label">Total Cars Owned</div>
        </div>
      </div>

      <h2>Status Breakdown</h2>
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-value-inspection">${inspectionValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="card-row">
            <div className="summary-label">🚗 Inspection</div> 
            <div className="summary-label">{inspectionCount} cars</div> 
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-value-fixing">${fixingValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="card-row">
            <div className="summary-label">🔧 Fixing</div> 
            <div className="summary-label">{fixingCount} cars</div> 
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-value-rfs">${rfsValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="card-row">
            <div className="summary-label">🏷️ Ready For Sale</div> 
            <div className="summary-label">{rfsCount} cars</div> 
          </div>
        </div>
      </div>

      <h2>Inventory</h2>
      <div className="search-bar">
    <input
    type="text"
    placeholder="Search by make, model, year, stock, or VIN..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    />
    <div className="filter-buttons">
    {['Inspection', 'Fixing', 'Ready For Sale', 'Sold'].map((state) => (
      <button
        key={state}
        className={`filter-button ${filters[state] ? 'active' : ''}`}
        onClick={() =>
          setFilters((prev) => ({ ...prev, [state]: !prev[state] }))
        }
      >
        {state}
      </button>
    ))}
    </div>
    </div>

      <div className="list-section">
          {/* Replace this with your actual list of cars */}
        <div className="car-card-list">
          {filteredCars.map((car) => (
            <div
              className={`car-card ${car.state === 'Sold' ? 'sold-car' : ''}`}
              key={car.id}
              onClick={() => {
                setSelectedCar(car);
                setEditForm(car);
                setPendingImageFiles([]);
                setImagesToDelete([]);
                supabase
                  .from('car_images')
                  .select('*')
                  .eq('car_id', car.id)
                  .order('display_order', { ascending: true })
                  .then(({ data }) => setCarImages(data || []));
              }}
              style={{ cursor: 'pointer' }}
            >
            <div className="car-card-header">
                <strong>{car.year} {car.make} {car.model}</strong>
                <span className="car-card-stock">Stock #{car.stock}</span>
              </div>
              <div className="car-card-details">
                <div><strong>VIN:</strong> {car.vin}</div>
                <div><strong>Purchase Date:</strong> {car.dateBought ? new Date(car.dateBought).toLocaleDateString() : '-'}</div>
                <div><strong>Purchase Price:</strong> ${Number(car.purchasePrice).toLocaleString()}</div>
                <div><strong>State:</strong> {car.state}</div>
                <div className="car-card-tags">
                  <span className={car.onLot ? 'tag tag-green' : 'tag tag-red'}>On Lot</span>
                  <span className={car.onWeb ? 'tag tag-green' : 'tag tag-red'}>On Web</span>
                  <span className={car.onCL ? 'tag tag-green' : 'tag tag-red'}>On CL</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
      <ReactModal
        isOpen={!!selectedCar}
        onRequestClose={() => {
          setSelectedCar(null);
          setCarImages([]);
          setPendingImageFiles([]);
          setImagesToDelete([]);
        }}
        contentLabel="Edit Car"
        ariaHideApp={false}
        className="modal"
        overlayClassName="overlay"
        >
        <h2>Edit Car Information</h2>
        <form
          onSubmit={async (e) => {
            e.preventDefault();

            if (!editForm.vin) {
              alert('VIN is required');
              return;
            }

            setSaving(true);
            let result;

            if (selectedCar?.isNew) {
              const cleanedForm = Object.fromEntries(
                Object.entries(editForm).filter(([_, value]) => value !== '' && value !== null)
              );
              result = await supabase.from('cars').insert(cleanedForm).select();
            } else {
              const cleanedForm = Object.fromEntries(
                Object.entries(editForm).filter(([_, value]) => value !== '' && value !== null)
              );

              result = await supabase
                .from('cars')
                .update(cleanedForm)
                .eq('id', selectedCar.id);
            }

            const { error } = result;

            if (error) {
              setSaving(false);
              alert(`${selectedCar?.isNew ? 'Insert' : 'Update'} failed: ` + error.message);
            } else {
              let carId = selectedCar?.id;
              if (selectedCar?.isNew && result.data?.[0]?.id) {
                carId = result.data[0].id;
              } else if (selectedCar?.isNew) {
                const { data: newCar } = await supabase
                  .from('cars')
                  .select('id')
                  .eq('vin', editForm.vin)
                  .single();
                carId = newCar?.id;
              }

              if (carId) {
                for (const file of pendingImageFiles) {
                  const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]/g, '_');
                  const base = `${carId}/${Date.now()}_${baseName}`;

                  let resized: { blob: Blob; ext: string; mime: string }[];
                  try {
                    resized = await Promise.all([
                      resizeImage(file, PREVIEW_EDGE, 0.82),
                      resizeImage(file, DISPLAY_EDGE, 0.8),
                    ]);
                  } catch (err) {
                    console.error('Image resize failed:', file.name, err);
                    alert(`Could not process ${file.name}. It was skipped.`);
                    continue;
                  }

                  const [preview, display] = resized;
                  const previewPath = `${base}.${preview.ext}`;
                  const displayPath = `${base}_display.${display.ext}`;
                  const variants = [
                    { path: previewPath, blob: preview.blob, mime: preview.mime },
                    { path: displayPath, blob: display.blob, mime: display.mime },
                  ];

                  const uploaded: string[] = [];
                  let uploadFailed = false;
                  for (const variant of variants) {
                    const { error: uploadError } = await supabase.storage
                      .from('car-images')
                      .upload(variant.path, variant.blob, {
                        contentType: variant.mime,
                        cacheControl: '31536000',
                      });
                    if (uploadError) {
                      console.error('Upload failed:', variant.path, uploadError);
                      uploadFailed = true;
                      break;
                    }
                    uploaded.push(variant.path);
                  }

                  // Don't leave half an image behind if the second variant fails.
                  if (uploadFailed) {
                    if (uploaded.length) {
                      await supabase.storage.from('car-images').remove(uploaded);
                    }
                    alert(`Upload failed for ${file.name}. It was skipped.`);
                    continue;
                  }

                  await supabase.from('car_images').insert({
                    car_id: carId,
                    storage_path: previewPath,
                    display_path: displayPath,
                    display_order: 0,
                  });
                }

                for (const imageId of imagesToDelete) {
                  const img = carImages.find((i: any) => i.id === imageId);
                  if (img) {
                    const paths = [img.storage_path, img.display_path].filter(Boolean);
                    await supabase.storage.from('car-images').remove(paths);
                    await supabase.from('car_images').delete().eq('id', imageId);
                  }
                }
              }

              await fetchCarsAndImages();
              setSaving(false);
              setSelectedCar(null);
              setCarImages([]);
              setPendingImageFiles([]);
              setImagesToDelete([]);
            }
          }}

          className="edit-form"
        >
        <div className="form-row">
            <label>
              Stock:
              <input
                type="text"
                value={editForm.stock}
                onChange={(e) =>
                setEditForm({ ...editForm, stock: e.target.value })
                }
              />
            </label>
            <label>
              Vin:
              <input
                type="text"
                value={editForm.vin}
                onChange={(e) =>
                setEditForm({ ...editForm, vin: e.target.value })
                }
              />
            </label>
            <label>
              State:
              <select
                value={editForm.state}
                onChange={(e) =>
                  setEditForm({ ...editForm, state: e.target.value })
                }
              >
                <option value="Inspection">Inspection</option>
                <option value="Fixing">Fixing</option>
                <option value="Ready For Sale">Ready For Sale</option>
                <option value="Sold">Sold</option>
              </select>
            </label>
        </div>
        <div className="form-row">
          <label>
          Year:
          <input
            type="number"
            value={editForm.year}
            onChange={(e) =>
              setEditForm({ ...editForm, year: e.target.value })
            }
          />
          </label>
          <label>
            Make:
            <input
              type="text"
              value={editForm.make}
              onChange={(e) =>
                setEditForm({ ...editForm, make: e.target.value })
              }
            />
          </label>

          <label>
            Model:
            <input
              type="text"
              value={editForm.model}
              onChange={(e) =>
                setEditForm({ ...editForm, model: e.target.value })
              }
            />
          </label>
        </div>
        <div className="form-row">
        <label>
          Date Bought:
          <input
            type="date"
            value={editForm.dateBought}
            onChange={(e) =>
              setEditForm({ ...editForm, dateBought: e.target.value })
            }
          />
        </label>
        <label>
          Date Sold:
          <input
            type="date"
            value={editForm.dateSold}
            onChange={(e) =>
              setEditForm({ ...editForm, dateSold: e.target.value })
            }
          />
        </label>
        </div>
        <div className="form-row">
        <label className="input-with-prefix">
          Purchase Price:
          <div className="input-wrapper">
            <span className="prefix">$</span>
            <input
              type="text"
              value={editForm.purchasePrice}
              onChange={(e) =>
                setEditForm({ ...editForm, purchasePrice: e.target.value })
              }
            />
          </div>
        </label>

        <label className="input-with-prefix">
          Sale Price:
          <div className="input-wrapper">
            <span className="prefix">$</span>
            <input
              type="text"
              value={editForm.salePrice || ''}
              onChange={(e) =>
                setEditForm({ ...editForm, salePrice: e.target.value })
              }
            />
          </div>
        </label>
        </div>
        <div className="form-row">
        <label>
          Acquired From:
          <input
            type="text"
            value={editForm.acquiredFrom}
            onChange={(e) =>
              setEditForm({ ...editForm, acquiredFrom: e.target.value })
            }
          />
        </label>
        </div>
        <div className="form-row">
        <label>
          Notes:
          <input
            type="text"
            value={editForm.notes}
            onChange={(e) =>
              setEditForm({ ...editForm, notes: e.target.value })
            }
          />
        </label>
        </div>
        <div className="form-row">
        <label>
          Features for Website:
          <input
            type="text"
            value={editForm.features}
            onChange={(e) =>
              setEditForm({ ...editForm, features: e.target.value })
            }
          />
        </label>
        </div>
        <div className="image-section">
          <label>Images:</label>
          <div className="image-grid">
            {carImages
              .filter((img: any) => !imagesToDelete.includes(img.id))
              .map((img: any) => {
                const bucket = supabase.storage.from('car-images');
                // display_path is null for images uploaded before variants existed.
                const thumbUrl = bucket.getPublicUrl(
                  img.display_path || img.storage_path
                ).data.publicUrl;
                const fullUrl = bucket.getPublicUrl(img.storage_path).data.publicUrl;
                return (
                  <div key={img.id} className="image-thumb-wrapper">
                    <img
                      src={thumbUrl}
                      alt="Car"
                      className="image-thumb"
                      loading="lazy"
                      width={100}
                      height={100}
                      onClick={() => setLightboxUrl(fullUrl)}
                      style={{ cursor: 'pointer' }}
                    />
                    <button
                      type="button"
                      className="image-remove-btn"
                      onClick={() =>
                        setImagesToDelete([...imagesToDelete, img.id])
                      }
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            {pendingImageFiles.map((file, i) => (
              <div key={`pending-${i}`} className="image-thumb-wrapper">
                <img
                  src={URL.createObjectURL(file)}
                  alt="Pending"
                  className="image-thumb"
                  onClick={() => setLightboxUrl(URL.createObjectURL(file))}
                  style={{ cursor: 'pointer' }}
                />
                <button
                  type="button"
                  className="image-remove-btn"
                  onClick={() =>
                    setPendingImageFiles(
                      pendingImageFiles.filter((_, idx) => idx !== i)
                    )
                  }
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) {
                setPendingImageFiles([
                  ...pendingImageFiles,
                  ...Array.from(e.target.files),
                ]);
                e.target.value = '';
              }
            }}
          />
          <button
            type="button"
            className="add-image-btn"
            onClick={() => document.getElementById('image-upload')?.click()}
          >
            + Add Image
          </button>
        </div>

        <div className="toggle-pills-row">
          <button
            type="button"
            className={`toggle-pill ${editForm.onLot ? 'toggle-pill-active' : ''}`}
            onClick={() => setEditForm({ ...editForm, onLot: !editForm.onLot })}
          >
            On Lot
          </button>
          <button
            type="button"
            className={`toggle-pill ${editForm.onWeb ? 'toggle-pill-active' : ''}`}
            onClick={() => setEditForm({ ...editForm, onWeb: !editForm.onWeb })}
          >
            On Web
          </button>
          <button
            type="button"
            className={`toggle-pill ${editForm.onCL ? 'toggle-pill-active' : ''}`}
            onClick={() => setEditForm({ ...editForm, onCL: !editForm.onCL })}
          >
            On Craigslist
          </button>
        </div>

        <div className="form-row" style={{ paddingTop: '25px' }}>
          <button
            className='button-cancel'
            type="button"
            onClick={() => {
              setSelectedCar(null);
              setCarImages([]);
              setPendingImageFiles([]);
              setImagesToDelete([]);
            }}
          >
            Cancel
          </button>
          <button className='button-save' type="submit" disabled={saving}>
            {saving ? <span className="spinner" /> : 'Save Changes'}
          </button>
        </div>
        </form>
        </ReactModal>
      {lightboxUrl && (
        <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Full size" className="lightbox-img" />
        </div>
      )}
    </>
  );
}